import { SettingNames } from '@/dashboards/models/interface-settings-models';
import { Logo } from '@/logo/models/logo';
import { ClientRepository } from '@/organizations/corporate/repositories/client-repository';
import { ColorUtils } from '@/utils/color-utils';
import {
    primaryColor,
    fourthColor,
    thirdColor,
    thirdColorLight,
    mpTextColorLight,
    secondaryColor,
    darkColor,
    lightColor,
    mpTextColorDark,
    dangerColor,
    successColor,
    warningColor
} from '@/core/new-crm-ui-colors';

export interface LayoutInitializationConfig {
    featureStore: {
        isHcmNewCrmUi: boolean;
        isNewTaskModal: boolean;
    };
    interfaceSettingsMap: Map<SettingNames, { value?: string | number | boolean }>;
    hasWhiteLabel: boolean;
}

export interface LayoutInitializationResult {
    globalColors: Record<string, string>;
    whiteLabelLogo: Logo | null;
    imageUrl: string;
}

export class LayoutUtils {
    private clientRepository: ClientRepository;

    constructor(clientRepository?: ClientRepository) {
        this.clientRepository = clientRepository || new ClientRepository();
    }

    /**
     * Apply CSS classes to HTML and body elements based on feature flags
     */
    applyCssClasses(config: LayoutInitializationConfig): void {
        const bodyElement = document.getElementsByTagName('body')[0];
        const htmlElement = document.getElementsByTagName('html')[0];

        // Always add moment-path classes
        bodyElement.classList.add('moment-path');
        htmlElement.classList.add('moment-path');

        htmlElement.classList.add('new-ui');
        bodyElement.classList.add('new-ui');

        if (config.featureStore.isHcmNewCrmUi) {
            bodyElement.classList.add('hcm-active');
        } else {
            bodyElement.classList.remove('hcm-active');
        }

        if (config.featureStore.isNewTaskModal) {
            htmlElement.classList.add('task-modal');
            bodyElement.classList.add('task-modal');
        } else {
            htmlElement.classList.remove('task-modal');
            bodyElement.classList.remove('task-modal');
        }
    }

    /**
     * Set CSS variables for fonts and base color
     */
    setCssFontVariables(): void {
        document.documentElement.style.setProperty('--body-font-family', "'Inter', sans-serif");
        document.documentElement.style.setProperty('--font-family-secondary', "'Inter', sans-serif");
        document.documentElement.style.setProperty('--base-color', mpTextColorDark);
    }

    /**
     * Get white label colors configuration
     */
    getWhiteLabelColors(interfaceSettingsMap: Map<SettingNames, { value?: string | number | boolean }>): Record<string, string> {
        const primary = '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_PRIMARY)?.value || '') as string;
        const secondary = '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SECONDARY)?.value || '') as string;

        return {
            '--primary-color': primary,
            '--secondary-color': secondary,
            '--third-color': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_THIRD)?.value || '') as string,
            '--fourth-color': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_FOURTH)?.value || '') as string,
            '--fifth-color': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_FIFTH)?.value || '') as string,
            '--sixth-color': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SIXTH)?.value || '') as string,
            '--seventh-color': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SEVENTH)?.value || '') as string,
            '--primary-dark': ColorUtils.darken(primary, 15),
            '--primary-superdark': ColorUtils.darken(primary, 30),
            '--primary-supersuperdark': ColorUtils.darken(primary, 40),
            '--primary-midlight': ColorUtils.lighten(primary, 20),
            '--primary-light': ColorUtils.lighten(primary, 40),
            '--primary-superlight': ColorUtils.lighten(primary, 60),
            '--primary-supersuperlight': ColorUtils.lighten(primary, 80),
            '--primary-ultralight': ColorUtils.adjustColorOpacity(primary, 15),
            '--primary-light10': ColorUtils.lighten(primary, 10),
            '--primary-light20': ColorUtils.lighten(primary, 20),
            '--primary-light30': ColorUtils.lighten(primary, 30),
            '--primary-light40': ColorUtils.lighten(primary, 40),
            '--primary-light50': ColorUtils.lighten(primary, 50),
            '--secondary-light': ColorUtils.lighten(secondary, 40),
            '--secondary-light30': ColorUtils.lighten(secondary, 30),
            '--secondary-light50': ColorUtils.lighten(secondary, 50),
            '--fc-meeting': '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_THIRD)?.value || '') as string,
            '--fc-meetinglight': ColorUtils.lighten('#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_THIRD)?.value || '') as string, 80),
            '--primary-color-20-opacity': ColorUtils.adjustColorOpacity(primary, 20),
            '--primary-color-40-opacity': ColorUtils.adjustColorOpacity(primary, 40),
            '--primary-color-50-opacity': ColorUtils.adjustColorOpacity(primary, 50),
            '--secondary-color-50-opacity': ColorUtils.adjustColorOpacity(secondary, 50)
        };
    }

    /**
     * Set white label CSS variables on body element
     */
    setWhiteLabelBodyStyles(interfaceSettingsMap: Map<SettingNames, { value?: string | number | boolean }>): void {
        const primary = '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_PRIMARY)?.value || '') as string;
        document.body.style.setProperty('--primary-color', primary);
        document.body.style.setProperty('--secondary-color', '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SECONDARY)?.value || '') as string);
        document.body.style.setProperty('--success-color', '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SUCCESS)?.value || '') as string);
        document.body.style.setProperty('--danger-color', '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_DANGER)?.value || '') as string);
        document.body.style.setProperty('--warning-color', '#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_WARNING)?.value || '') as string);
        document.body.style.setProperty('--success-color-15-opacity', ColorUtils.adjustColorOpacity('#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_SUCCESS)?.value || '') as string, 15));
        document.body.style.setProperty('--warning-color-15-opacity', ColorUtils.adjustColorOpacity('#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_WARNING)?.value || '') as string, 15));
        document.body.style.setProperty('--danger-color-15-opacity', ColorUtils.adjustColorOpacity('#' + (interfaceSettingsMap.get(SettingNames.WHITE_LABEL_DANGER)?.value || '') as string, 15));
        document.body.style.setProperty('--primary-color-20-opacity', ColorUtils.adjustColorOpacity(primary, 20));
        document.body.style.setProperty('--primary-color-40-opacity', ColorUtils.adjustColorOpacity(primary, 40));
    }

    /**
     * Get default global colors for non-white-label new CRM UI
     */
    getDefaultNewCrmUiColors(): Record<string, string> {
        return {
            '--primary-color': primaryColor,
            '--third-color': thirdColor,
            '--fourth-color': fourthColor,
            '--third-color-light': thirdColorLight,
            '--light-color': lightColor,
            '--mp-text-color-dark': mpTextColorDark,
            '--secondary-color': secondaryColor,
            '--dark-color': darkColor,
            '--mp-text-color-light': mpTextColorLight,
            '--success-color': successColor,
            '--danger-color': dangerColor,
            '--warning-color': warningColor,
            '--success-color-15-opacity': ColorUtils.adjustColorOpacity(successColor, 15),
            '--warning-color-15-opacity': ColorUtils.adjustColorOpacity(warningColor, 15),
            '--danger-color-15-opacity': ColorUtils.adjustColorOpacity(dangerColor, 15),
            '--primary-color-20-opacity': ColorUtils.adjustColorOpacity(primaryColor, 20),
            '--primary-color-40-opacity': ColorUtils.adjustColorOpacity(primaryColor, 40)
        };
    }

    /**
     * Get fallback colors for white label with new CRM UI when logo fetch fails
     */
    getFallbackWhiteLabelColors(): Record<string, string> {
        return {
            '--mp-text-color-light': mpTextColorLight,
            '--secondary-color': secondaryColor
        };
    }

    /**
     * Load white label logo and generate image URL
     */
    async loadWhiteLabelLogo(): Promise<{ logo: Logo | null; imageUrl: string }> {
        try {
            const whiteLabelLogo = await this.clientRepository.getWhiteLabelLogo();

            if (whiteLabelLogo) {
                const content = await this.clientRepository.getWhiteLabelLogoContent();
                if (content && whiteLabelLogo.mime_type) {
                    const fileBlob = new Blob([content], { type: whiteLabelLogo.mime_type });
                    const imageUrl = URL.createObjectURL(fileBlob);
                    return { logo: whiteLabelLogo, imageUrl };
                }
            }
        } catch (error) {
            // Do nothing
        }

        return { logo: null, imageUrl: '/img/logos/ll_brandmark.svg' };
    }

    /**
     * Initialize layout with all necessary configurations
     */
    async initializeLayout(config: LayoutInitializationConfig): Promise<LayoutInitializationResult> {
        const result: LayoutInitializationResult = {
            globalColors: {},
            whiteLabelLogo: null,
            imageUrl: '/img/logos/ll_brandmark.svg'
        };

        // Apply CSS classes
        this.applyCssClasses(config);

        // Set font CSS variables
        this.setCssFontVariables();

        // Handle white label configuration
        if (config.hasWhiteLabel) {
            // Set white label colors
            result.globalColors = this.getWhiteLabelColors(config.interfaceSettingsMap);

            // Set body styles for white label
            this.setWhiteLabelBodyStyles(config.interfaceSettingsMap);

            // Load white label logo
            const logoResult = await this.loadWhiteLabelLogo();
            result.whiteLabelLogo = logoResult.logo;
            result.imageUrl = logoResult.imageUrl;

            // If logo failed to load but we're using new CRM UI, set fallback colors
            if (!logoResult.logo) {
                result.globalColors = this.getFallbackWhiteLabelColors();
            }
        } else {
            // Non-white label configuration
            result.imageUrl = '/img/logos/ll_brandmark.svg';

            result.globalColors = this.getDefaultNewCrmUiColors();
        }

        return result;
    }
}
